import { useState } from "react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Loader2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import PhoneField from "../components/PhoneField";

const MAX_FILE_BYTES = 25 * 1024 * 1024;

async function uploadApplicantPhoto(file) {
  if (file.size > MAX_FILE_BYTES) throw new Error(`${file.name} is over 25 MB`);
  const fd = new FormData();
  fd.append("file", file);
  const r = await api.post("/applications/upload", fd);
  return r.data.url;
}

export default function Apply() {
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", age: "", gender: "women",
    city: "", country: "", height_cm: "", instagram: "", biography: "", experience: "",
  });
  const [photos, setPhotos] = useState([]);  // [{ url, name, uploading?, error? }]
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [reference, setReference] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (photos.length + files.length > 4) {
      toast.error("Maximum 4 photos");
      return;
    }
    for (const file of files) {
      if (file.size > MAX_FILE_BYTES) { toast.error(`${file.name} is over 25 MB`); continue; }
      // Add a placeholder tile with local preview while we upload
      const localPreview = URL.createObjectURL(file);
      const tile = { url: null, preview: localPreview, name: file.name, uploading: true };
      setPhotos((p) => [...p, tile]);
      try {
        const url = await uploadApplicantPhoto(file);
        setPhotos((p) => p.map((t) => (t.preview === localPreview ? { ...t, url, uploading: false } : t)));
      } catch (err) {
        toast.error(err?.response?.data?.detail || err.message || `Failed: ${file.name}`);
        setPhotos((p) => p.filter((t) => t.preview !== localPreview));
        URL.revokeObjectURL(localPreview);
      }
    }
  };

  const removePhoto = (i) => {
    const tile = photos[i];
    if (tile?.preview) URL.revokeObjectURL(tile.preview);
    setPhotos(photos.filter((_, j) => j !== i));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.age || !form.city || !form.country) {
      toast.error("Please fill in all required fields"); return;
    }
    const ready = photos.filter((p) => p.url && !p.uploading);
    if (ready.length < 4) {
      toast.error("Please upload all 4 required digitals (see brief above)"); return;
    }
    if (photos.some((p) => p.uploading)) {
      toast.error("Please wait for photos to finish uploading"); return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/applications", {
        ...form,
        age: Number(form.age),
        height_cm: form.height_cm ? Number(form.height_cm) : null,
        photos: ready.map((p) => p.url),
      });
      setReference(r.data.reference);
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="pt-40 max-w-[1600px] mx-auto px-6 lg:px-12 min-h-[60vh]">
        <p className="label-overline text-zinc-500">Application received</p>
        <h1 className="font-serif text-6xl lg:text-8xl tracking-tighter font-light mt-6 leading-[0.9]">
          Thank you<span className="italic text-zinc-600">.</span>
        </h1>
        <p className="text-zinc-700 mt-8 max-w-2xl text-lg leading-relaxed">
          Our scouts review every application carefully. If your profile aligns with our roster
          we will be in touch within 14 days. We wish you the very best.
        </p>
        {reference && (
          <div className="mt-12 border border-black/15 bg-white p-8 max-w-xl">
            <p className="label-overline text-zinc-500">Your reference</p>
            <p className="font-mono text-3xl mt-2 tracking-wider" data-testid="application-reference">{reference}</p>
            <p className="text-sm text-zinc-600 mt-4" data-testid="application-receipt-note">
              We've also emailed this reference to <span className="text-black">{form.email}</span> — if you don't see it within a few minutes, check your spam folder.
            </p>
            <p className="text-sm text-zinc-600 mt-2">
              You can check the status of your application at any time on the{" "}
              <a href="/status" className="link-underline">status page</a> using this code and your email.
            </p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div data-testid="apply-page" className="pt-32">
      {/* HERO with editorial side video */}
      <section className="max-w-[1600px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <p className="label-overline text-zinc-500">Open Casting · Get Scouted</p>
          <h1 className="font-serif text-6xl md:text-7xl lg:text-8xl tracking-tighter font-light mt-6 leading-[0.9]">
            Get<br/><span className="italic text-zinc-600">scouted.</span>
          </h1>
          <p className="text-zinc-700 mt-8 max-w-xl leading-relaxed text-lg">
            Ege Models is a model management agency based in Rotterdam — we sign new faces,
            develop their book, and place them with clients and partner agencies. We review every submission individually.
          </p>

          <div className="mt-10 flex flex-wrap gap-3">
            <a href="#application-form" className="btn-primary">Start your application</a>
            <a href="#photo-rules" className="btn-ghost">How to take the photos</a>
          </div>
        </div>
        <div className="lg:col-span-5">
          <div className="relative aspect-[4/5] overflow-hidden bg-[#0A0A0A]">
            <img
              src="/hero/hero-runway.jpg"
              alt="Ege Models talent"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent pointer-events-none"/>
            <div className="absolute bottom-5 left-5 z-10 text-white">
              <p className="label-overline text-white/70">Ege Models</p>
              <p className="font-serif italic text-2xl mt-1">The canvas</p>
            </div>
          </div>
        </div>
      </section>

      {/* PHOTO RULES — strict agency brief */}
      <section id="photo-rules" className="max-w-[1200px] mx-auto px-6 lg:px-12 mt-24 scroll-mt-32">
        <div className="border border-black/15 bg-[#FAFAFA] p-10 md:p-14">
          <p className="label-overline text-zinc-500">How to take your digitals</p>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tighter font-light mt-3 leading-[0.95]">
            Four photos.<br/><span className="italic text-zinc-600">The canvas.</span>
          </h2>
          <p className="text-zinc-700 mt-6 leading-relaxed max-w-2xl">
            International agencies look at digitals before anything else. Please follow these
            instructions precisely — they are the same brief used by top agencies worldwide.
          </p>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4">
            <Rule num="01" t="Headshot — straight on" d="Hair down, no makeup, neutral expression, eyes to camera. From shoulders up."/>
            <Rule num="02" t="Headshot — profile" d="Same as above, but rotated 90°. Show the side profile from shoulders up."/>
            <Rule num="03" t="Full length — straight on" d="Hands relaxed at your sides, weight evenly distributed, neutral expression."/>
            <Rule num="04" t="Full length — profile" d="Same as above, rotated 90°. Show your full side profile head to toe."/>
          </div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <Rule num="·" t="No makeup, no filters" d="No retouching of any kind. Just you."/>
            <Rule num="·" t="Natural light" d="Daylight indoors or shade outside. Avoid harsh overheads."/>
            <Rule num="·" t="Fitted tank top + jeans" d="No baggy clothes, no patterns, no accessories. Hair pulled back if it's covering your face."/>
          </div>
        </div>
      </section>

      <section id="application-form" className="max-w-[1200px] mx-auto px-6 lg:px-12 mt-24 scroll-mt-32">
        <p className="label-overline text-zinc-500">Application form</p>
        <h2 className="font-serif text-4xl md:text-5xl tracking-tighter font-light mt-3 leading-[0.95]">
          Tell us about<br/><span className="italic text-zinc-600">yourself.</span>
        </h2>

        <form onSubmit={submit} className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-6">
          <Field label="Full name *">
            <Input data-testid="apply-name" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} className="bg-transparent border-black/20 rounded-none focus-visible:ring-0 focus-visible:border-black"/>
          </Field>
          <Field label="Email *">
            <Input data-testid="apply-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="bg-transparent border-black/20 rounded-none focus-visible:ring-0 focus-visible:border-black"/>
          </Field>
          <Field label="Phone (with country code, for WhatsApp)">
            <PhoneField value={form.phone} onChange={(v) => set("phone", v)} testId="apply-phone"/>
          </Field>
          <Field label="Age *">
            <Input data-testid="apply-age" type="number" value={form.age} onChange={(e) => set("age", e.target.value)} className="bg-transparent border-black/20 rounded-none focus-visible:ring-0 focus-visible:border-black"/>
          </Field>
          <Field label="Division *">
            <Select value={form.gender} onValueChange={(v) => set("gender", v)}>
              <SelectTrigger data-testid="apply-gender" className="bg-transparent border-black/20 rounded-none focus:ring-0 focus:border-black">
                <SelectValue/>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="women">Women</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Height (cm)">
            <Input data-testid="apply-height" type="number" value={form.height_cm} onChange={(e) => set("height_cm", e.target.value)} className="bg-transparent border-black/20 rounded-none focus-visible:ring-0 focus-visible:border-black"/>
          </Field>
          <Field label="City *">
            <Input data-testid="apply-city" value={form.city} onChange={(e) => set("city", e.target.value)} className="bg-transparent border-black/20 rounded-none focus-visible:ring-0 focus-visible:border-black"/>
          </Field>
          <Field label="Country *">
            <Input data-testid="apply-country" value={form.country} onChange={(e) => set("country", e.target.value)} className="bg-transparent border-black/20 rounded-none focus-visible:ring-0 focus-visible:border-black"/>
          </Field>
          <Field label="Instagram">
            <Input data-testid="apply-instagram" value={form.instagram} onChange={(e) => set("instagram", e.target.value)} placeholder="@handle" className="bg-transparent border-black/20 rounded-none focus-visible:ring-0 focus-visible:border-black"/>
          </Field>
          <div className="sm:col-span-2">
            <Field label="A short biography (optional)">
              <Textarea data-testid="apply-bio" rows={3} value={form.biography} onChange={(e) => set("biography", e.target.value)} placeholder="Tell us about yourself — where you're from, what you love, what makes you you…" className="bg-transparent border-black/20 rounded-none focus-visible:ring-0 focus-visible:border-black"/>
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Field label="Modeling experience (optional)">
              <Textarea data-testid="apply-exp" rows={3} value={form.experience} onChange={(e) => set("experience", e.target.value)} placeholder="Any previous shoots, runways, agencies, training…" className="bg-transparent border-black/20 rounded-none focus-visible:ring-0 focus-visible:border-black"/>
            </Field>
          </div>

          <div className="sm:col-span-2">
            <Label className="label-overline text-zinc-600">Digitals * (4 specific shots — see brief above · JPG / PNG / HEIC · up to 25 MB each)</Label>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3">
              {photos.map((p, i) => (
                <div key={i} className="relative aspect-[3/4] bg-[#F5F5F5] overflow-hidden">
                  <img src={p.preview || p.url} alt={p.name} className="w-full h-full object-cover" />
                  {p.uploading && (
                    <div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center text-white">
                      <Loader2 size={18} className="animate-spin"/>
                      <span className="label-overline text-[10px] mt-1">Uploading…</span>
                    </div>
                  )}
                  <button type="button" onClick={() => removePhoto(i)} data-testid={`apply-remove-photo-${i}`} className="absolute top-1 right-1 bg-black/70 text-white p-1">
                    <X size={14}/>
                  </button>
                </div>
              ))}
              {photos.length < 4 && (
                <label data-testid="apply-upload-label" className="aspect-[3/4] border border-dashed border-black/30 flex flex-col items-center justify-center text-zinc-600 hover:text-black hover:border-black cursor-pointer transition-colors">
                  <Upload size={20}/>
                  <span className="label-overline mt-2">Upload</span>
                  <input data-testid="apply-upload" type="file" accept="image/*" multiple className="hidden" onChange={handleFiles}/>
                </label>
              )}
            </div>
          </div>

          <div className="sm:col-span-2 flex justify-end mt-4">
            <button type="submit" disabled={submitting} data-testid="apply-submit" className="btn-primary">
              {submitting && <Loader2 size={14} className="animate-spin"/>} Submit Application
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="label-overline text-zinc-600">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function Rule({ num, t, d }) {
  return (
    <div className="bg-white border border-black/10 p-5">
      <p className="font-mono text-xs text-zinc-500">{num}</p>
      <p className="font-serif text-xl mt-1">{t}</p>
      <p className="text-zinc-600 text-sm mt-2 leading-relaxed">{d}</p>
    </div>
  );
}
